import { useState, useEffect } from "react";
import {
  Paper,
  Stack,
  Box,
  Typography,
  Tab,
  IconButton,
  Tooltip,
} from "@mui/material";
import { TabContext, TabPanel, TabList } from "@mui/lab";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import CodeIcon from "@mui/icons-material/Code";
import LinkIcon from "@mui/icons-material/Link";

import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import jsx from "react-syntax-highlighter/dist/esm/languages/prism/jsx";
import prism from "react-syntax-highlighter/dist/esm/styles/prism/vsc-dark-plus";
SyntaxHighlighter.registerLanguage("jsx", jsx);

const TabKeys = {
  Demo: "demo",
  Code: "code",
};

const ComponentExample = ({
  name,
  description,
  import: demoImport,
  rawImport: codeImport,
}) => {
  const [tab, setTab] = useState(
    demoImport ? TabKeys.Demo : codeImport ? TabKeys.Code : ""
  );
  const [demo, setDemo] = useState(null);
  const [code, setCode] = useState(null);

  useEffect(() => {
    if (!demoImport) return;
    const loadDemo = async () => {
      const imported = await demoImport;
      if (imported?.default) {
        const DemoComponent = imported.default;
        setDemo(<DemoComponent />);
      }
    };
    loadDemo();
  }, [demoImport]);

  useEffect(() => {
    if (!codeImport) return;
    const loadCode = async () => {
      const imported = await codeImport;
      if (imported?.default) {
        setCode(imported.default);
      }
    };
    loadCode();
  }, [codeImport]);

  const id = name
    ?.toLowerCase()
    .replace(/\/|\(|\)/g, " ")
    .trim()
    .replace(/\s+/g, "-");

  return (
    <Paper id={id} sx={{ mt: 4 }}>
      <TabContext value={tab}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          pl={2}
          bgcolor="primary.main"
          color="primary.contrastText"
          sx={{
            ".example-link": {
              color: "primary.contrastText",
              opacity: 0,
              transition: "opacity 200ms",
            },
            "&:hover .example-link": {
              opacity: 1,
            },
          }}
        >
          <Typography fontWeight={500}>{name}</Typography>
          <Tooltip title="Copy Link">
            <IconButton
              component="a"
              href={`#${id}`}
              onClick={() => {
                setTimeout(() => {
                  navigator.clipboard.writeText(window.location.href);
                }, 100);
              }}
              className="example-link"
            >
              <LinkIcon />
            </IconButton>
          </Tooltip>
          <TabList
            onChange={(e, v) => setTab(v)}
            indicatorColor=""
            sx={{
              ml: "auto !important",
              ".MuiTab-root": {
                color: "primary.contrastText",
                minHeight: "initial",
                py: 2,
                "&.Mui-selected": {
                  bgcolor: "background.white",
                  color: "primary.main",
                },
              },
            }}
          >
            {demoImport && (
              <Tab
                label="Demo"
                icon={<SlideshowIcon />}
                iconPosition="start"
                value={TabKeys.Demo}
              />
            )}
            {codeImport && (
              <Tab
                label="Code"
                icon={<CodeIcon />}
                iconPosition="start"
                value={TabKeys.Code}
              />
            )}
          </TabList>
        </Stack>
        <Box>
          {demo && (
            <TabPanel value={TabKeys.Demo} sx={{ p: 2 }}>
              {description && (
                <Typography variant="body2" mb={3}>
                  {description}
                </Typography>
              )}
              {demo}
            </TabPanel>
          )}
          {code && (
            <TabPanel
              value={TabKeys.Code}
              sx={{
                p: 0,
                pre: { margin: "0 !important" },
                code: { bgcolor: "unset", fontSize: "0.9rem !important" },
              }}
            >
              <SyntaxHighlighter language="jsx" style={prism} wrapLongLines>
                {code}
              </SyntaxHighlighter>
            </TabPanel>
          )}
        </Box>
      </TabContext>
    </Paper>
  );
};

export default ComponentExample;
